import React from "react";
import { Droplets, TrendingUp, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { SEO } from "../components/SEO";
import { PageHeader } from "../components/PageHeader";
import { Reveal, Stagger, StaggerItem } from "../components/motion/Reveal";
import { SectionHeading } from "../components/motion/SectionHeading";
import { CTASection } from "../components/home/FinancialBenefits";

const CROP_IMAGES = [
  "https://images.unsplash.com/photo-1518635017498-87f514b751ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.unsplash.com/photo-1498557850523-fd3d118b962e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.unsplash.com/photo-1444392061186-9fc38f84f726?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
];

export default function AgriculturalInfrastructure() {
  const { t } = useLang();
  const a = t.agriInfra;

  return (
    <>
      <SEO pageKey="agricultural-infrastructure" title={t.seo.agriInfra.title} description={t.seo.agriInfra.description} />
      <PageHeader pageKey="agricultural-infrastructure"
        overline={a.overline}
        title={a.title}
        sub={a.sub}
        image="https://images.unsplash.com/photo-1473968512647-3e447244af8f?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
      />

      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 text-green mb-4"><Droplets className="w-5 h-5" /></div>
            <p className="text-charcoal/60 text-lg leading-relaxed">{a.intro}</p>
          </Reveal>
        </div>
      </section>

      {/* Crops */}
      <section className="pb-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Stagger className="grid grid-cols-2 md:grid-cols-3 gap-0">
            {a.crops.map((crop, i) => (
              <StaggerItem key={i}>
                <div className="group overflow-hidden bg-white border border-border hover:shadow-2xl transition-shadow duration-500 h-full" data-testid={`crop-${i}`}>
                  <div className="aspect-[4/3] overflow-hidden hover-zoom">
                    <img src={CROP_IMAGES[i]} alt={crop.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-3.5 sm:p-7">
                    <h3 className="font-display text-base sm:text-2xl text-charcoal mb-2 sm:mb-3 leading-tight group-hover:text-green-light transition-colors">{crop.name}</h3>
                    <p className="text-charcoal/55 text-[11px] sm:text-sm leading-snug sm:leading-relaxed">{crop.desc}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Case study distribution */}
      <section className="py-10 sm:py-14 bg-lightgray">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
          <SectionHeading overline={a.caseTitle} title={a.caseSub} />
          <div className="grid lg:grid-cols-2 gap-12 items-center mt-12">
            <Reveal>
              <div className="space-y-6">
                {a.distribution.map((d, i) => (
                  <div key={i} data-testid={`distribution-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-charcoal font-medium text-sm">{d.label}</span>
                      <span className="text-charcoal/50 text-sm">{d.value} · {d.pct}%</span>
                    </div>
                    <div className="h-3 rounded-full bg-white overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${d.pct}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: i * 0.15 }}
                        className={`h-full rounded-full ${i === 0 ? "bg-green" : i === 1 ? "bg-green-light" : "bg-gold"}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-3xl overflow-hidden hover-zoom">
                <img src="https://images.unsplash.com/photo-1500937386664-56d1dfef3854?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Agricultural land distribution" className="w-full h-[380px] object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 5-year returns */}
      <section className="py-10 sm:py-14 bg-charcoal text-white relative overflow-hidden grain">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 relative">
          <SectionHeading overline={a.returnsTitle} title={a.returnsSub} light />
          <Stagger className="grid grid-cols-3 gap-0 mt-12">
            {a.returns.map((r, i) => (
              <StaggerItem key={i}>
                <div className="bg-white/[0.04] border border-white/10 p-4 sm:p-8 text-center h-full" data-testid={`return-${i}`}>
                  <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-gold mx-auto mb-3 sm:mb-4" />
                  <div className="font-display text-2xl sm:text-5xl text-gold">{r.value}</div>
                  <div className="text-white/60 text-[10px] sm:text-sm mt-1.5 sm:mt-2">{r.label}</div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.2}>
            <p className="flex items-center justify-center gap-2 text-white/40 text-xs mt-8 text-center">
              <AlertTriangle className="w-3.5 h-3.5" /> {a.disclaimer}
            </p>
          </Reveal>
        </div>
      </section>
      <CTASection />
    </>
  );
}
